import { Subject, combineLatest, of } from 'rxjs'
import { map, filter, distinctUntilChanged, startWith, switchMap, catchError, tap } from 'rxjs/operators'
import { RESPONSE_UNAUTHORIZED, RESPONSE_PENDING, RESPONSE_BADRESPONSE } from 'front-end-core/constants/responses'
import observeRequest from 'helpers/fcObserveRequest'
import { SEARCH_HISTORY, USER, USER_AUTH } from 'constants/requests'
import { fetch, observe } from 'services/ApiService'
import { mergeResponses, isValidResponse, mapResponse } from 'front-end-core/helpers/dataflow'
import { URL_SEARCH_HISTORY, URL_PASSWORD_RESET, URL_USER_UPSERT } from 'constants/urls'
import { toAssociatedResourceCA } from 'mapping/post'
import { upsert, query, QUALIFIER_CA_PREFERENCES } from 'services/AssociatedResource'
import debug from 'front-end-core/core/debug'
import Route from 'route-parser'
import { AOL_CORPORATE } from 'constants/aol/aol'

const searchHistoryRoute = new Route(URL_SEARCH_HISTORY)
const passwordResetRoute = new Route(URL_PASSWORD_RESET)

const $user = fetch(USER)

export const User = combineLatest(
  [$user, fetch(USER_AUTH)],
  mergeResponses
)

export const $isLogged = $user.pipe(map(x =>
  x === RESPONSE_PENDING
    ? RESPONSE_PENDING
    : x === RESPONSE_UNAUTHORIZED
      ? false
      : isValidResponse(x)
), map(mapResponse(isLogged => ({ isLogged }))))

export const $userAOL = $user.pipe(map(x =>
  x === RESPONSE_PENDING
    ? RESPONSE_PENDING
    : x === RESPONSE_UNAUTHORIZED
      ? AOL_CORPORATE
      : isValidResponse(x)
), map(mapResponse(areaOfLaw => ({ areaOfLaw }))))

export const UserUrn = User.pipe(filter(isValidResponse), map(x => x.uri), distinctUntilChanged())

const PreferencesPoke = new Subject()
const PreferencesUrn = PreferencesPoke.pipe(startWith(true), switchMap(() => UserUrn))

export const SearchHistory = observe(SEARCH_HISTORY, { length: 20 })
export const pushSearchHistory = (searchTerm, searchType) =>
  observeRequest({
    url: searchHistoryRoute.reverse(),
    headers: { 'Content-Type': 'application/json' },
    method: 'post',
    body: JSON.stringify({ searchTerm, searchType })
  }).subscribe()

export const UserPreferences = PreferencesUrn.pipe(
  switchMap(urn => query(QUALIFIER_CA_PREFERENCES, urn)),
  catchError(e => debug.error(e) || of(RESPONSE_BADRESPONSE))
)

export const updateUserPreferences =
  (prefs) =>
    upsert(QUALIFIER_CA_PREFERENCES, toAssociatedResourceCA(prefs))
      .pipe(tap(() => PreferencesPoke.next(true)))

export const submitPasswordReset = (body) =>
  observeRequest({
    url: passwordResetRoute.reverse(),
    headers: { 'Content-Type': 'application/json' },
    method: 'post',
    body: JSON.stringify(body)
  }).pipe(map(resp => ({ success: resp !== RESPONSE_BADRESPONSE })))

export const updateUser = (body) =>
  observeRequest({
    url: URL_USER_UPSERT,
    headers: { 'Content-Type': 'application/json' },
    method: 'put',
    body: JSON.stringify(body)
  }).pipe(map(resp => ({ success: resp !== RESPONSE_BADRESPONSE })))
